import React, { useState } from 'react';
import {
  authSignUp,
  authConfirmSignUp,
  authSignIn,
  authResetPassword,
  authConfirmResetPassword,
} from './auth';

function AuthScreen({ onAuthenticated, prefilledEmail, onBack, initialMode }) {
  const [mode, setMode] = useState(initialMode === 'signUp' ? 'signUp' : 'signIn'); // signIn, signUp, confirmSignUp, forgotPassword, confirmReset
  const [email, setEmail] = useState(prefilledEmail || '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authSignIn(email, password);
      onAuthenticated();
    } catch (err) {
      setError(err.message || 'Sign in failed');
    }
    setLoading(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const cap = (v) => {
        const t = v.trim();
        return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
      };
      await authSignUp(email, password, cap(firstName), cap(lastName));
      setMode('confirmSignUp');
      setMessage('Check your email for a verification code.');
    } catch (err) {
      setError(err.message || 'Sign up failed');
    }
    setLoading(false);
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authConfirmSignUp(email, code);
      setMessage('Account confirmed! Please sign in.');
      setMode('signIn');
      setCode('');
    } catch (err) {
      setError(err.message || 'Confirmation failed');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authResetPassword(email);
      setMode('confirmReset');
      setMessage('Check your email for a reset code.');
    } catch (err) {
      setError(err.message || 'Password reset failed');
    }
    setLoading(false);
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await authConfirmResetPassword(email, code, password);
      setMessage('Password reset! Please sign in.');
      setMode('signIn');
      setCode('');
    } catch (err) {
      setError(err.message || 'Reset confirmation failed');
    }
    setLoading(false);
  };

  return (
    <div className="auth-screen">
      <div className="auth-container">
        {onBack && (
          <button type="button" className="auth-back" onClick={onBack}>
            ← Back to home
          </button>
        )}
        <div className="auth-header">
          <h1>AWS Certification Study Hub</h1>
          <p className="auth-subtitle">Sign in to track your progress</p>
        </div>

        {/* SSO context banner — only visible when arriving from the
            Learning Platform with a token. The two apps have separate
            Cognito pools today, so the platform login does not
            auto-pass through; the banner sets the right expectation
            and pre-fills the email so the user only types their
            password (or signs up if this is their first visit). */}
        {prefilledEmail && (
          <div className="auth-sso-banner" role="status">
            <span className="auth-sso-icon" aria-hidden="true">👋</span>
            <div className="auth-sso-body">
              <strong>You&apos;re signed into the Learning Platform as <span className="auth-sso-email">{prefilledEmail}</span>.</strong>
              <p>
                Sign in here (or sign up if this is your first time) to access the
                exam catalog. The Learning Platform and Dream&amp;Build Exams use
                separate accounts today, so a quick second sign-in is needed.
              </p>
            </div>
          </div>
        )}

        {error && <div className="auth-error">{error}</div>}
        {message && <div className="auth-message">{message}</div>}

        {mode === 'signIn' && (
          <form onSubmit={handleSignIn} className="auth-form">
            <h2>Sign In</h2>
            <label>
              Email
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
            </label>
            <label>
              Password
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            </label>
            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="auth-links">
              <button type="button" onClick={() => { setMode('signUp'); setError(''); setMessage(''); }}>
                Create an account
              </button>
              <button type="button" onClick={() => { setMode('forgotPassword'); setError(''); setMessage(''); }}>
                Forgot password?
              </button>
            </div>
          </form>
        )}

        {mode === 'signUp' && (
          <form onSubmit={handleSignUp} className="auth-form">
            <h2>Create Account</h2>
            <label>
              First Name
              <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required autoFocus autoComplete="given-name" />
            </label>
            <label>
              Last Name
              <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required autoComplete="family-name" />
            </label>
            <label>
              Email
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            </label>
            <label>
              Password
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
            </label>
            <label>
              Confirm Password
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
            </label>
            <p className="auth-hint">Min 8 characters, uppercase, lowercase, and a number.</p>
            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? 'Creating...' : 'Create Account'}
            </button>
            <div className="auth-links">
              <button type="button" onClick={() => { setMode('signIn'); setError(''); setMessage(''); }}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {mode === 'confirmSignUp' && (
          <form onSubmit={handleConfirmSignUp} className="auth-form">
            <h2>Verify Email</h2>
            <label>
              Verification Code
              <input type="text" value={code} onChange={e => setCode(e.target.value)} required autoFocus placeholder="Enter code from email" />
            </label>
            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <div className="auth-links">
              <button type="button" onClick={() => { setMode('signIn'); setError(''); setMessage(''); }}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {mode === 'forgotPassword' && (
          <form onSubmit={handleForgotPassword} className="auth-form">
            <h2>Reset Password</h2>
            <label>
              Email
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
            </label>
            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <div className="auth-links">
              <button type="button" onClick={() => { setMode('signIn'); setError(''); setMessage(''); }}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {mode === 'confirmReset' && (
          <form onSubmit={handleConfirmReset} className="auth-form">
            <h2>Set New Password</h2>
            <label>
              Reset Code
              <input type="text" value={code} onChange={e => setCode(e.target.value)} required autoFocus />
            </label>
            <label>
              New Password
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
            </label>
            <label>
              Confirm New Password
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
            </label>
            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <div className="auth-links">
              <button type="button" onClick={() => { setMode('signIn'); setError(''); setMessage(''); }}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        <div className="auth-disclaimer" role="contentinfo">
          <p>
            <strong>Not affiliated with Amazon Web Services.</strong> This is an
            independent study platform for educational purposes only. AWS, Amazon
            Web Services, and all related certification names and logos are
            trademarks of Amazon.com, Inc. or its affiliates. This platform is not
            endorsed by, sponsored by, or associated with AWS in any way. Practice
            questions are intended to aid learning and do not represent actual exam
            content.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AuthScreen;
